import { Link } from "@fluentui/react/lib/Link";
import { Stack } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";
import { WithTranslator } from "Components/Generic/Translator";
import { useCallback } from "react";

import { WithSearchContext } from "Components/Overview/Search/SearchContext";
import { EntityType } from "Model/EntityType";
import { getTranslatedFieldLabel } from "Utils/Field";
import { endsWithoutWhitespace, isNullOrWhiteSpace } from "Utils/String";
import { expressionAsString, getLastSearchTerm, parseExpression } from "Utils/Tokenizr";
import { ISuggesionProps } from "./ISuggestionProps";
import { useSuggestionStyles } from "./UseSuggestionStyles";

export const Suggestion = WithSearchContext(
  WithTranslator((props: ISuggesionProps): JSX.Element => {
    const {
      suggestion, translator, searchHighlight, setSearchTerm, setSearchHighlight, setFocusBackToSearchbox
    } = props;

    const styles = useSuggestionStyles();

    const generateOnClickSuggestion = useCallback(
      (fieldName: string) => () => {
        setSearchTerm((v) => combineSearchTerms(v, fieldName));
        setFocusBackToSearchbox(true);
        setSearchHighlight(null);
      },
      [setFocusBackToSearchbox, setSearchHighlight, setSearchTerm]
    );

    return (
      <Link key={suggestion} className={styles.root} onClick={generateOnClickSuggestion(suggestion)} role="listitem">
        <Stack.Item className={styles.translation}>
          <Text variant="medium" block nowrap>
            {getTranslatedFieldLabel(suggestion, EntityType.Project, translator, suggestion)}
          </Text>
        </Stack.Item>
        <Text className={styles.fieldName} variant="smallPlus" block nowrap>
          {createHighlightedText(suggestion, searchHighlight, styles)}
        </Text>
      </Link>
    );
  })
);

// TODO: remove any type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createHighlightedText(fieldName: string, searchHighlight: string, styles: any): JSX.Element {
  if (isNullOrWhiteSpace(fieldName) || isNullOrWhiteSpace(searchHighlight)) {
    return <span>{fieldName}</span>;
  }

  const firstIndex = fieldName.toLowerCase().indexOf(searchHighlight);
  const prefix = fieldName.slice(0, firstIndex);
  const marking = fieldName.slice(firstIndex, firstIndex + searchHighlight.length);
  const suffix = fieldName.slice(firstIndex + searchHighlight.length);

  return (
    <span>
      {prefix}
      <span className={styles.highlight}>{marking}</span>
      {suffix}
    </span>
  );
}

function preparePreviousSearch(search: string): string {
  if (isNullOrWhiteSpace(search)) {
    return ``;
  }

  const expression = expressionAsString(
    endsWithoutWhitespace(search) ? getLastSearchTerm(search) : parseExpression(search)
  );

  return isNullOrWhiteSpace(expression) ? `` : `${expression} `;
}

function combineSearchTerms(previousSearch: string, fieldName: string): string {
  if (fieldName.startsWith(previousSearch)) {
    return `${fieldName}:`;
  }

  return `${preparePreviousSearch(previousSearch)}${fieldName}:`;
}
